import * as React from "react";
import { ArrowRight, CheckCircle2, Loader2, RefreshCw } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@eduviaos/ui-kit";
import type { StudentImportSummary } from "@eduviaos/shared";
import { useTerms } from "@/hooks/useTerms";

/**
 * Phase H6d — shared post-commit success card extracted from the
 * wizard. The wizard always exits to the dashboard (no point in
 * re-importing during onboarding — the principal has skipped or
 * committed once); the standing surface routinely re-imports
 * additional batches, so it shows a "Import another batch" CTA
 * instead.
 *
 * The parent owns both CTA callbacks. Either or both can be supplied:
 *   - `onContinue` renders the primary CTA (label customisable via
 *     `continueLabel`). Hidden when undefined.
 *   - `onReset` renders a secondary "Import another batch" CTA. Hidden
 *     when undefined.
 *
 * Per Iron Rule #6 the entrance animation is opacity + 8px slide-in,
 * 500ms — short enough not to feel ceremonial after the previous
 * commit spinner already paid the cognitive bill.
 */
export interface DoneCardProps {
  summary: StudentImportSummary;
  onContinue?: () => void;
  continueLabel?: React.ReactNode;
  onReset?: () => void;
  isPending?: boolean;
}

export function DoneCard({
  summary,
  onContinue,
  continueLabel,
  onReset,
  isPending = false,
}: DoneCardProps) {
  const { terms } = useTerms();
  const fresh = summary.fresh_rows;
  return (
    <Card className="motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-3 mt-8 motion-safe:duration-500">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <CheckCircle2 className="text-primary size-5" aria-hidden="true" />
          Imported {fresh} {(fresh === 1 ? terms.learner.one : terms.learner.many).toLowerCase()}
        </CardTitle>
        <CardDescription>
          {summary.duplicate_rows > 0 || summary.invalid_rows > 0 ? (
            <>
              {summary.duplicate_rows > 0 ? (
                <>
                  Skipped {summary.duplicate_rows} duplicate
                  {summary.duplicate_rows === 1 ? "" : "s"}.{" "}
                </>
              ) : null}
              {summary.invalid_rows > 0 ? (
                <>
                  Ignored {summary.invalid_rows} invalid row
                  {summary.invalid_rows === 1 ? "" : "s"}.{" "}
                </>
              ) : null}
              Fix them in the file and re-import later if you want to bring them in.
            </>
          ) : (
            <>Every row landed cleanly.</>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-3">
        {onContinue ? (
          <Button
            type="button"
            size="lg"
            onClick={onContinue}
            disabled={isPending}
            className="group mx-auto w-full max-w-sm gap-2 transition-transform duration-150 ease-out active:scale-[0.97]"
            data-testid="student-import-continue"
          >
            {isPending ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                Finishing setup…
              </>
            ) : (
              <>
                {continueLabel ?? "Continue"}
                <ArrowRight
                  className="size-4 transition-transform duration-200 ease-out group-hover:translate-x-0.5"
                  aria-hidden="true"
                />
              </>
            )}
          </Button>
        ) : null}
        {onReset ? (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onReset}
            disabled={isPending}
            className="mx-auto w-full max-w-sm gap-2"
            data-testid="student-import-another"
          >
            <RefreshCw className="size-3.5" aria-hidden="true" />
            Import another batch
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}
